import type {
  BusinessGetResponseType,
  UserCreateResponseType,
} from "@sellar-io/api-v2/types";
import type { Auth, User } from "firebase/auth";
import { createContext } from "react";
import type { SessionInitializationFailure } from "../hooks/sessionInitializationFailure";

export type ExtendedFirebaseUser = {
  uid: string;
  isAdmin: boolean;
  hasPasswordAuth: boolean;
  firebaseUser: User;
};

export type FirebaseSession = {
  user: ExtendedFirebaseUser | undefined;
  setUser: (user: Pick<ExtendedFirebaseUser, "hasPasswordAuth">) => void;
  loading: boolean;
  error?: SessionInitializationFailure;
  retry: () => void;
  auth: Auth;
};

export type SellarUserSession = {
  user: UserCreateResponseType | null;
  changeBusiness: (businessId: number) => void;
  signOut: () => Promise<void>;
  loading: boolean;
  error?: SessionInitializationFailure;
};

export type CurrentBusinessSession = {
  business: BusinessGetResponseType | null;
  loading: boolean;
  error?: SessionInitializationFailure;
};

export type SellarSessionState =
  | { status: "retrying-initialization"; failure: SessionInitializationFailure }
  | { status: "initialization-error"; failure: SessionInitializationFailure }
  | { status: "restoring-authentication" }
  | { status: "anonymous" }
  | { status: "loading-user"; firebaseUser: ExtendedFirebaseUser }
  | { status: "needs-user"; firebaseUser: ExtendedFirebaseUser }
  | {
      status: "loading-business";
      firebaseUser: ExtendedFirebaseUser;
      sellarUser: UserCreateResponseType;
    }
  | {
      status: "needs-business";
      firebaseUser: ExtendedFirebaseUser;
      sellarUser: UserCreateResponseType;
    }
  | {
      status: "ready";
      firebaseUser: ExtendedFirebaseUser;
      sellarUser: UserCreateResponseType;
      business: BusinessGetResponseType;
    };

export type SellarSessionSlices = {
  firebase: FirebaseSession;
  sellar: SellarUserSession;
  currentBusiness: CurrentBusinessSession;
};

export type SellarSessionContextType = SellarSessionSlices & {
  state: SellarSessionState;
  retryInitialization: () => void;
};

export const getSellarSessionState = ({
  firebase,
  sellar,
  currentBusiness,
}: {
  firebase: Pick<FirebaseSession, "user" | "loading" | "error">;
  sellar: Pick<SellarUserSession, "user" | "loading" | "error">;
  currentBusiness: CurrentBusinessSession;
}): SellarSessionState => {
  if (firebase.error)
    return { status: "initialization-error", failure: firebase.error };
  if (firebase.loading) return { status: "restoring-authentication" };
  if (!firebase.user) return { status: "anonymous" };
  if (sellar.error)
    return { status: "initialization-error", failure: sellar.error };
  if (sellar.loading) {
    return { status: "loading-user", firebaseUser: firebase.user };
  }
  if (!sellar.user) {
    return { status: "needs-user", firebaseUser: firebase.user };
  }
  if (currentBusiness.error)
    return { status: "initialization-error", failure: currentBusiness.error };
  if (currentBusiness.loading) {
    return {
      status: "loading-business",
      firebaseUser: firebase.user,
      sellarUser: sellar.user,
    };
  }
  if (!currentBusiness.business) {
    return {
      status: "needs-business",
      firebaseUser: firebase.user,
      sellarUser: sellar.user,
    };
  }
  return {
    status: "ready",
    firebaseUser: firebase.user,
    sellarUser: sellar.user,
    business: currentBusiness.business,
  };
};

export const SellarSessionContext = createContext<
  SellarSessionContextType | undefined
>(undefined);
